/**
 * Generato automaticamente.
 * framework-cmp-domande
 */
import { useQuery } from 'react-query';
import type {
  QueryFunction,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient.ts';
import type { ErrorType } from '../../../client/apiClient.ts';
import type { Competenza } from '../schemas/competenza.ts';
import type { WsProblema } from '../schemas/wsProblema.ts';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Restituisce l'elenco delle competenze per le quali l'utente può effettuare una ricerca. Considera solo i tipi domanda ricercabili (come nella home frontoffice).
 * @summary Elenco competenze ricercabili
 */
export const getFrontCompetenzeRicercabili = (
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<Competenza[]>(
    {
      url: `/framework-cmp-domande/competenze-ricercabili`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontCompetenzeRicercabiliQueryKey = () =>
  [`/framework-cmp-domande/competenze-ricercabili`] as const;

export const getGetFrontCompetenzeRicercabiliQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontCompetenzeRicercabili>>,
  TError = ErrorType<WsProblema>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getFrontCompetenzeRicercabili>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontCompetenzeRicercabili>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontCompetenzeRicercabiliQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontCompetenzeRicercabili>>
  > = ({ signal }) => getFrontCompetenzeRicercabili(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetFrontCompetenzeRicercabiliQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontCompetenzeRicercabili>>
>;
export type GetFrontCompetenzeRicercabiliQueryError = ErrorType<WsProblema>;

/**
 * @summary Elenco competenze ricercabili
 */
export const useGetFrontCompetenzeRicercabili = <
  TData = Awaited<ReturnType<typeof getFrontCompetenzeRicercabili>>,
  TError = ErrorType<WsProblema>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getFrontCompetenzeRicercabili>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontCompetenzeRicercabiliQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
